// src/components/WhataNews.js
// ✅ VERSIÓN CORREGIDA - Arreglados problemas de useEffect y useCallback
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, User, Calendar, Filter, FileText, Newspaper, BookOpen, Lightbulb } from 'lucide-react';
import { useTheme } from './ThemeContext';
import ThemeToggle from './ThemeToggle';
import { Helmet } from 'react-helmet';
import whataLogo from '../assets/icons/brand/whatalab-logo.svg';
import './WhataNews.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// ✅ DEBUG: Log para verificar la URL del API
console.log('[WhataNews] API_BASE:', API_BASE);

// Mapeo de tipos de plantilla para mostrar
const TEMPLATE_LABELS = {
  multiNews: { label: 'Multi-Noticias', icon: FileText },
  longForm: { label: 'Artículo', icon: Newspaper },
  tutorial: { label: 'Tutorial', icon: BookOpen },
  analysis: { label: 'Análisis', icon: Lightbulb },
};

// Chemical Formulas Component for WhataBlog background
const WhataBlogFormulas = () => {
  const formulas = [
    { formula: "H₃BAR", category: "protocol", x: 5, y: 15 },
    { formula: "WL₄B₆", category: "whatalab", x: 85, y: 20 },
    { formula: "SC₃H₄", category: "token", x: 15, y: 75 },
    { formula: "DP₂GC₃", category: "nft", x: 75, y: 80 },
    { formula: "DLT₂O₃", category: "protocol", x: 45, y: 10 },
    { formula: "WS₃NIP₄", category: "whatalab", x: 25, y: 45 },
    { formula: "K₂BL₅", category: "token", x: 70, y: 50 },
    { formula: "WT₆IGR", category: "nft", x: 10, y: 30 },
    { formula: "HTS₂O", category: "protocol", x: 90, y: 60 },
    { formula: "WK₄IT₃", category: "whatalab", x: 55, y: 85 },
    { formula: "HBX₃O₂", category: "token", x: 35, y: 25 },
    { formula: "Si₄WAS₃", category: "nft", x: 65, y: 35 },
    { formula: "NET₂₀₂₄", category: "protocol", x: 8, y: 65 },
    { formula: "WB₂OST₄", category: "whatalab", x: 80, y: 12 },
    { formula: "S₃NTX₄", category: "token", x: 50, y: 55 },
    { formula: "B₃AHC₄", category: "nft", x: 20, y: 90 },
  ];

  return (
    <div className="whatablog-formulas-container">
      {formulas.map((item, index) => (
        <motion.div
          key={index}
          className={`whatablog-formula whatablog-formula--${item.category}`}
          style={{ left: `${item.x}%`, top: `${item.y}%` }}
          animate={{
            opacity: [0.5, 0.8, 0.4, 0.7, 0.3, 0.6, 0.5],
            scale: [1, 1.1, 0.95, 1.05, 0.9, 1.02, 1],
            x: [0, 25, -20, 30, -15, 20, 0],
            y: [0, -20, 25, -25, 20, -15, 0],
          }}
          transition={{
            duration: 18 + index * 0.8,
            delay: index * 0.2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {item.formula}
        </motion.div>
      ))}
    </div>
  );
};

// Blog Post Card Component
const BlogPostCard = ({ post, onClick, index }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const templateType = post.templateType || 'multiNews';
  const templateInfo = TEMPLATE_LABELS[templateType] || TEMPLATE_LABELS.multiNews;
  const TemplateIcon = templateInfo.icon;

  return (
    <motion.article
      className="whatablog-card"
      onClick={onClick}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.35 }}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
    >
      <div className="whatablog-card-image">
        {post.bannerUrl ? (
          <img src={post.bannerUrl} alt={post.title} loading="lazy" />
        ) : (
          <div className="whatablog-card-placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {post.featured && <span className="whatablog-card-featured">★ Destacado</span>}
        <span className={`whatablog-card-type whatablog-card-type--${templateType}`}>
          <TemplateIcon size={12} />
          {templateInfo.label}
        </span>
      </div>
      <div className="whatablog-card-content">
        <div className="whatablog-card-meta">
          <span className="whatablog-card-date">
            <Calendar size={14} />
            {formatDate(post.publishedAt)}
          </span>
          <span className="whatablog-card-views">
            <Eye size={14} />
            {post.views}
          </span>
        </div>
        <h3 className="whatablog-card-title">{post.title}</h3>
        {post.excerpt && <p className="whatablog-card-excerpt">{post.excerpt}</p>}
        {post.authorName && (
          <span className="whatablog-card-author">
            <User size={14} />
            {post.authorName}
          </span>
        )}
      </div>
    </motion.article>
  );
};

// Filters Component
const BlogFilters = ({
  languageFilter,
  setLanguageFilter,
  publisherFilter,
  setPublisherFilter,
  dateFilter,
  setDateFilter,
  typeFilter,
  setTypeFilter,
  publishers
}) => {
  return (
    <motion.div
      className="whatablog-filters"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
    >
      <div className="whatablog-filters-header">
        <Filter size={18} />
        <span>Filtros</span>
      </div>

      <div className="whatablog-filters-grid">
        <div className="whatablog-filter-group">
          <label className="whatablog-filter-label">Tipo</label>
          <select
            className="whatablog-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="multiNews">Multi-Noticias</option>
            <option value="longForm">Artículo Largo</option>
            <option value="tutorial">Tutorial</option>
            <option value="analysis">Análisis</option>
          </select>
        </div>

        <div className="whatablog-filter-group">
          <label className="whatablog-filter-label">Idioma</label>
          <select
            className="whatablog-filter-select"
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
        </div>

        <div className="whatablog-filter-group">
          <label className="whatablog-filter-label">Autor</label>
          <select
            className="whatablog-filter-select"
            value={publisherFilter}
            onChange={(e) => setPublisherFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            {publishers.map(publisher => (
              <option key={publisher} value={publisher}>{publisher}</option>
            ))}
          </select>
        </div>

        <div className="whatablog-filter-group">
          <label className="whatablog-filter-label">Fecha</label>
          <select
            className="whatablog-filter-select"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">Todas</option>
            <option value="week">Última semana</option>
            <option value="month">Último mes</option>
            <option value="3months">Últimos 3 meses</option>
            <option value="year">Último año</option>
          </select>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== RENDERIZADORES DE CONTENIDO POR PLANTILLA ====================

// Renderizador Multi-Noticias (formato original)
const MultiNewsRenderer = ({ post }) => {
  const parseNewsContent = (post) => {
    if (!post) return [];

    const newsArray = [];
    for (let i = 1; i <= 5; i++) {
      const title = post[`news${i}Title`];
      const body = post[`news${i}Body`];

      if (title && body) {
        newsArray.push({
          title,
          body,
          url: post[`news${i}Url`] || '',
          urlText: post[`news${i}UrlText`] || '',
          imageUrl: post[`news${i}ImageUrl`] || '',
          imageSize: post[`news${i}ImageSize`] || 'medium',
        });
      }
    }

    return newsArray;
  };

  const newsItems = parseNewsContent(post);

  if (newsItems.length === 0) {
    return <p className="whatablog-news-empty">No hay noticias disponibles en este artículo.</p>;
  }

  return (
    <div className="whatablog-news-list">
      {newsItems.map((news, idx) => (
        <motion.div
          key={idx}
          className="whatablog-news-item"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 * idx }}
        >
          <div className="whatablog-news-number">{idx + 1}</div>
          <div className="whatablog-news-content">
            <h3 className="whatablog-news-title">{news.title}</h3>

            {news.imageUrl && (
              <div className={`whatablog-news-image whatablog-news-image--${news.imageSize}`}>
                <img src={news.imageUrl} alt={news.title} loading="lazy" />
              </div>
            )}

            <div
              className="whatablog-news-body"
              dangerouslySetInnerHTML={{ __html: news.body.replace(/\n/g, '<br/>') }}
            />

            {news.url && (
              <a
                href={news.url}
                target="_blank"
                rel="noopener noreferrer"
                className="whatablog-news-link"
              >
                {news.urlText || 'Leer más'} →
              </a>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Renderizador Long Form (artículo largo)
const LongFormRenderer = ({ content }) => {
  if (!content || !content.sections) {
    return <p className="whatablog-news-empty">No hay contenido disponible.</p>;
  }

  return (
    <div className="whatablog-longform">
      {content.sections.map((section, idx) => {
        switch (section.type) {
          case 'heading':
            return (
              <motion.h2
                key={idx}
                className="whatablog-longform-heading"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * idx }}
              >
                {section.content}
              </motion.h2>
            );

          case 'paragraph':
            return (
              <motion.p
                key={idx}
                className="whatablog-longform-paragraph"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                dangerouslySetInnerHTML={{ __html: section.content.replace(/\n/g, '<br/>') }}
              />
            );

          case 'quote':
            return (
              <motion.blockquote
                key={idx}
                className="whatablog-longform-quote"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * idx }}
              >
                {section.content}
                {section.author && <cite>— {section.author}</cite>}
              </motion.blockquote>
            );

          case 'image':
            return (
              <motion.figure
                key={idx}
                className="whatablog-longform-figure"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * idx }}
              >
                <img src={section.url} alt={section.caption || ''} loading="lazy" />
                {section.caption && <figcaption>{section.caption}</figcaption>}
              </motion.figure>
            );

          case 'list':
            return (
              <motion.ul
                key={idx}
                className="whatablog-longform-list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 * idx }}
              >
                {section.items?.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </motion.ul>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

// Renderizador Tutorial
const TutorialRenderer = ({ content }) => {
  if (!content || !content.steps) {
    return <p className="whatablog-news-empty">No hay pasos disponibles en este tutorial.</p>;
  }

  return (
    <div className="whatablog-tutorial">
      {content.steps.map((step, idx) => (
        <motion.div
          key={idx}
          className="whatablog-tutorial-step"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 * idx }}
        >
          <div className="whatablog-tutorial-number">
            <span>{idx + 1}</span>
          </div>
          <div className="whatablog-tutorial-content">
            <h3 className="whatablog-tutorial-title">{step.title}</h3>
            <div
              className="whatablog-tutorial-body"
              dangerouslySetInnerHTML={{ __html: step.content.replace(/\n/g, '<br/>') }}
            />
            {step.imageUrl && (
              <div className="whatablog-tutorial-image">
                <img src={step.imageUrl} alt={step.title} loading="lazy" />
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Renderizador Análisis
const AnalysisRenderer = ({ content }) => {
  if (!content) {
    return <p className="whatablog-news-empty">No hay análisis disponible.</p>;
  }

  return (
    <div className="whatablog-analysis">
      {content.introduction && (
        <motion.div
          className="whatablog-analysis-intro"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p dangerouslySetInnerHTML={{ __html: content.introduction.replace(/\n/g, '<br/>') }} />
        </motion.div>
      )}

      {content.keyPoints && content.keyPoints.length > 0 && (
        <div className="whatablog-analysis-points">
          <h3 className="whatablog-analysis-section-title">Puntos Clave</h3>
          {content.keyPoints.map((point, idx) => (
            <motion.div
              key={idx}
              className="whatablog-analysis-point"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * idx }}
            >
              <div className="whatablog-analysis-point-icon">💡</div>
              <div className="whatablog-analysis-point-content">
                <h4>{point.title}</h4>
                <p dangerouslySetInnerHTML={{ __html: point.content.replace(/\n/g, '<br/>') }} />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {content.conclusion && (
        <motion.div
          className="whatablog-analysis-conclusion"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="whatablog-analysis-section-title">Conclusión</h3>
          <p dangerouslySetInnerHTML={{ __html: content.conclusion.replace(/\n/g, '<br/>') }} />
        </motion.div>
      )}
    </div>
  );
};

// ==================== BLOG POST VIEW ====================

const BlogPostView = ({ slug, onBack }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('[BlogPostView] Fetching post with slug:', slug);
        console.log('[BlogPostView] URL:', `${API_BASE}/blog/slug/${slug}`);
        
        const res = await fetch(`${API_BASE}/blog/slug/${slug}`);
        
        console.log('[BlogPostView] Response status:', res.status);
        
        if (!res.ok) {
          throw new Error(`Post no encontrado (${res.status})`);
        }
        
        const data = await res.json();
        console.log('[BlogPostView] Post loaded:', data.title);
        setPost(data);
      } catch (err) {
        console.error('[BlogPostView] Error:', err);
        setError(err.message || 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const parseTemplateContent = (post) => {
    if (!post || !post.templateContent) return null;

    try {
      return typeof post.templateContent === 'string'
        ? JSON.parse(post.templateContent)
        : post.templateContent;
    } catch (e) {
      console.error('Error parsing templateContent:', e);
      return null;
    }
  };

  const renderContent = () => {
    if (!post) return null;

    const templateType = post.templateType || 'multiNews';
    const templateContent = parseTemplateContent(post);

    switch (templateType) {
      case 'multiNews':
        return <MultiNewsRenderer post={post} />;

      case 'longForm':
        return <LongFormRenderer content={templateContent} />;

      case 'tutorial':
        return <TutorialRenderer content={templateContent} />;

      case 'analysis':
        return <AnalysisRenderer content={templateContent} />;

      default:
        if (post.news1Title) {
          return <MultiNewsRenderer post={post} />;
        } else if (templateContent?.sections) {
          return <LongFormRenderer content={templateContent} />;
        } else if (templateContent?.steps) {
          return <TutorialRenderer content={templateContent} />;
        } else if (templateContent?.keyPoints) {
          return <AnalysisRenderer content={templateContent} />;
        }
        return <p className="whatablog-news-empty">No se pudo determinar el formato del contenido.</p>;
    }
  };

  if (loading) {
    return (
      <div className="whatablog-post-loading">
        <div className="whatablog-loading-spinner"></div>
        <p>Cargando artículo...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <motion.div
        className="whatablog-post-error"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2>Artículo no encontrado</h2>
        <p>{error}</p>
        <button className="whatablog-btn whatablog-btn--primary" onClick={onBack}>
          Volver al blog
        </button>
      </motion.div>
    );
  }

  const templateType = post.templateType || 'multiNews';
  const templateInfo = TEMPLATE_LABELS[templateType] || TEMPLATE_LABELS.multiNews;
  const TemplateIcon = templateInfo.icon;

  return (
    <>
      <Helmet>
        <title>{post.metaTitle || post.title} - WhataNews</title>
        <meta name="description" content={post.metaDescription || post.excerpt || `Noticias del ${formatDate(post.publishedAt)}`} />
        <meta property="og:title" content={post.ogTitle || post.metaTitle || post.title} />
        <meta property="og:description" content={post.ogDescription || post.metaDescription || post.excerpt || `Noticias del ${formatDate(post.publishedAt)}`} />
        {(post.ogImage || post.bannerUrl) && <meta property="og:image" content={post.ogImage || post.bannerUrl} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${window.location.origin}/whatanews/${post.slug}`} />
        <meta name="twitter:card" content={post.twitterCard || "summary_large_image"} />
        <meta name="twitter:title" content={post.ogTitle || post.metaTitle || post.title} />
        <meta name="twitter:description" content={post.ogDescription || post.metaDescription || post.excerpt || `Noticias del ${formatDate(post.publishedAt)}`} />
        {(post.ogImage || post.bannerUrl) && <meta name="twitter:image" content={post.ogImage || post.bannerUrl} />}
      </Helmet>

      <motion.article
        className={`whatablog-post-view whatablog-post-view--${templateType}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <button className="whatablog-post-back" onClick={onBack}>
          <ArrowLeft size={18} />
          Volver al blog
        </button>

        {post.bannerUrl && (
          <motion.div
            className="whatablog-post-banner"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <img src={post.bannerUrl} alt={post.title} />
          </motion.div>
        )}

        <header className="whatablog-post-header">
          <div className="whatablog-post-meta">
            <span className={`whatablog-post-type whatablog-post-type--${templateType}`}>
              <TemplateIcon size={14} />
              {templateInfo.label}
            </span>
            <span className="whatablog-post-date">
              <Calendar size={16} />
              {formatDate(post.publishedAt)}
            </span>
            {post.authorName && (
              <span className="whatablog-post-author">
                <User size={16} />
                {post.authorName}
              </span>
            )}
            <span className="whatablog-post-views">
              <Eye size={16} />
              {post.views} lecturas
            </span>
          </div>
          <h1 className="whatablog-post-title">{post.title}</h1>
          {post.excerpt && <p className="whatablog-post-excerpt">{post.excerpt}</p>}
        </header>

        <div className="whatablog-post-content">
          {renderContent()}
        </div>
      </motion.article>
    </>
  );
};

// ==================== MAIN COMPONENT ====================

const WhataNews = () => {
  const { theme } = useTheme();
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  
  // ✅ FIX: Usar useRef para offset para evitar recreación de useCallback
  const offsetRef = useRef(0);
  const LIMIT = 12;

  const [languageFilter, setLanguageFilter] = useState('all');
  const [publisherFilter, setPublisherFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // ✅ FIX: useCallback sin offset en dependencias, usando ref
  const fetchPosts = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentOffset = reset ? 0 : offsetRef.current;
      const url = `${API_BASE}/blog?status=published&limit=${LIMIT}&offset=${currentOffset}`;
      
      console.log('[WhataNews] Fetching posts from:', url);
      
      const res = await fetch(url);
      
      console.log('[WhataNews] Response status:', res.status);
      
      if (!res.ok) {
        throw new Error(`Error al cargar noticias (${res.status})`);
      }
      
      const data = await res.json();
      
      console.log('[WhataNews] Posts received:', data.posts?.length || 0);
      console.log('[WhataNews] hasMore:', data.hasMore);
      
      // Validar estructura de respuesta
      if (!data.posts || !Array.isArray(data.posts)) {
        console.error('[WhataNews] Invalid response structure:', data);
        throw new Error('Respuesta del servidor inválida');
      }

      setPosts(prev => reset ? data.posts : [...prev, ...data.posts]);
      setHasMore(data.hasMore || false);
      
      // Actualizar offset ref
      if (reset) {
        offsetRef.current = data.posts.length;
      } else {
        offsetRef.current = currentOffset + data.posts.length;
      }
      
    } catch (err) {
      console.error('[WhataNews] Fetch error:', err);
      setError(err.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, []); // ✅ Sin dependencias - usa ref para offset

  // ✅ FIX: useEffect con fetchPosts en dependencias
  useEffect(() => {
    if (!slug) {
      console.log('[WhataNews] No slug, fetching posts list...');
      fetchPosts(true);
    }
  }, [slug, fetchPosts]); // ✅ Incluir fetchPosts

  const publishers = useMemo(() => {
    const uniquePublishers = [...new Set(posts.map(p => p.authorName).filter(Boolean))];
    return uniquePublishers.sort();
  }, [posts]);

  const filteredPosts = useMemo(() => {
    let filtered = [...posts];

    // Filtro por tipo de plantilla
    if (typeFilter !== 'all') {
      filtered = filtered.filter(post => {
        const postType = post.templateType || 'multiNews';
        return postType === typeFilter;
      });
    }

    // Filtro por idioma
    if (languageFilter !== 'all') {
      filtered = filtered.filter(post =>
        post.language?.toLowerCase() === languageFilter.toLowerCase()
      );
    }

    // Filtro por autor
    if (publisherFilter !== 'all') {
      filtered = filtered.filter(post => post.authorName === publisherFilter);
    }

    // Filtro por fecha
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case '3months':
          filterDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }

      filtered = filtered.filter(post => {
        const postDate = new Date(post.publishedAt);
        return postDate >= filterDate;
      });
    }

    return filtered;
  }, [posts, languageFilter, publisherFilter, dateFilter, typeFilter]);

  const handlePostClick = (postSlug) => {
    navigate(`/whatanews/${postSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    navigate('/whatanews');
  };

  // Vista de post individual
  if (slug) {
    return (
      <div className="whatablog-page" data-theme={theme}>
        <WhataBlogFormulas />

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35 }}
        >
          <Link to="/" className="whatablog-back-btn">
            <ArrowLeft size={18} />
            <span>Back to WhataLab</span>
          </Link>
        </motion.div>

        <motion.div
          className="whatablog-theme-toggle"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35 }}
        >
          <ThemeToggle />
        </motion.div>

        <div className="whatablog-container whatablog-container--post">
          <BlogPostView slug={slug} onBack={handleBack} />
        </div>

        <motion.div
          className="whatablog-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          © 2025 <Link to="/">WhataLab</Link>. Built on Hedera.
        </motion.div>
      </div>
    );
  }

  // Vista de lista de posts
  return (
    <div className="whatablog-page" data-theme={theme}>
      <WhataBlogFormulas />

      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Link to="/" className="whatablog-back-btn">
          <ArrowLeft size={18} />
          <span>Back to WhataLab</span>
        </Link>
      </motion.div>

      <motion.div
        className="whatablog-theme-toggle"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35 }}
      >
        <ThemeToggle />
      </motion.div>

      <div className="whatablog-container">
        <motion.div
          className="whatablog-logo-wrapper"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
        >
          <img src={whataLogo} alt="WhataLab" className="whatablog-logo-icon" />
        </motion.div>

        <motion.div
          className="whatablog-badge"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Eye size={16} />
          Your daily break in the ecosystem
        </motion.div>

        <motion.h1
          className="whatablog-title"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          WhataNews
        </motion.h1>

        <motion.p
          className="whatablog-subtitle"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Noticias, actualizaciones y análisis del ecosistema WhataLab y la comunidad Hedera.
        </motion.p>

        <BlogFilters
          languageFilter={languageFilter}
          setLanguageFilter={setLanguageFilter}
          publisherFilter={publisherFilter}
          setPublisherFilter={setPublisherFilter}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          publishers={publishers}
        />

        {error && (
          <motion.div
            className="whatablog-error-banner"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p>⚠️ {error}</p>
            <p style={{ fontSize: '0.8em', opacity: 0.8 }}>
              API URL: {API_BASE}
            </p>
            <button onClick={() => fetchPosts(true)}>Reintentar</button>
          </motion.div>
        )}

        {loading && posts.length === 0 ? (
          <motion.div
            className="whatablog-loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="whatablog-loading-spinner"></div>
            <p>Cargando noticias...</p>
            <p style={{ fontSize: '0.8em', opacity: 0.6, marginTop: '0.5rem' }}>
              Conectando a: {API_BASE}
            </p>
          </motion.div>
        ) : filteredPosts.length === 0 ? (
          <motion.div
            className="whatablog-empty"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <h2>No se encontraron noticias</h2>
            {posts.length === 0 ? (
              <p>No hay noticias publicadas todavía, o hubo un problema al cargarlas.</p>
            ) : (
              <p>Intenta ajustar los filtros para ver más resultados.</p>
            )}
            {posts.length === 0 && (
              <button 
                className="whatablog-btn whatablog-btn--secondary"
                onClick={() => fetchPosts(true)}
                style={{ marginTop: '1rem' }}
              >
                Reintentar carga
              </button>
            )}
          </motion.div>
        ) : (
          <>
            <motion.section
              className="whatablog-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {filteredPosts.map((post, index) => (
                <BlogPostCard
                  key={post.id}
                  post={post}
                  index={index}
                  onClick={() => handlePostClick(post.slug)}
                />
              ))}
            </motion.section>

            {hasMore && (
              <motion.div
                className="whatablog-load-more"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <button
                  className="whatablog-btn whatablog-btn--primary"
                  onClick={() => fetchPosts(false)}
                  disabled={loading}
                >
                  {loading ? 'Cargando...' : 'Cargar más noticias'}
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>

      <motion.div
        className="whatablog-footer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        © 2025 <Link to="/">WhataLab</Link>. Built on Hedera.
      </motion.div>
    </div>
  );
};

export default WhataNews;